/**
 * Webhook authentication fields (ENG-1737).
 *
 * Mirrors actuate_admin's WebhookCustomer auth fields
 * (api/serializers/webhook/webhook_customer_base_serializer.py):
 * auth_type, auth_endpoint, auth_body, auth_token_path.
 *
 * auth_body and auth_token_path are credentials: a reader without
 * view_passwords gets them back as WEBHOOK_SECRET_MASK. The server treats an
 * echoed auth_body mask as "unchanged", but NOT an echoed auth_token_path mask
 * (it would save the dots), so the forms only ever send a secret the user
 * actually edited.
 */

import { AxiosError } from "axios";

/** actuate_admin's FAKE_PASSWORD. */
export const WEBHOOK_SECRET_MASK = "•••••••••";

export type WebhookAuthType = "basic" | "digest" | "bearer";

export const WEBHOOK_AUTH_TYPE_OPTIONS: Array<{
  value: "" | WebhookAuthType;
  label: string;
}> = [
  { value: "", label: "None" },
  { value: "basic", label: "Basic" },
  { value: "digest", label: "Digest" },
  { value: "bearer", label: "Bearer Token" },
];

export type WebhookAuthField =
  | "auth_type"
  | "auth_endpoint"
  | "auth_body"
  | "auth_token_path";

export interface WebhookAuthValues {
  auth_type: string | null;
  auth_endpoint: string | null;
  auth_body: string | null;
  auth_token_path: string | null;
}

export const EMPTY_WEBHOOK_AUTH_VALUES: WebhookAuthValues = {
  auth_type: null,
  auth_endpoint: null,
  auth_body: null,
  auth_token_path: null,
};

/** field name -> first server error message for that field */
export type WebhookFieldErrors = Partial<Record<string, string>>;

/** Lower-cased, trimmed auth type; "" when unset (matches the server). */
export const normalizeAuthType = (authType: string | null | undefined) =>
  authType ? authType.trim().toLowerCase() : "";

export const authTypeLabel = (authType: string | null | undefined) => {
  const normalized = normalizeAuthType(authType);
  const option = WEBHOOK_AUTH_TYPE_OPTIONS.find(
    (candidate) => candidate.value === normalized
  );
  return option ? option.label : authType || "None";
};

export const isBearer = (authType: string | null | undefined) =>
  normalizeAuthType(authType) === "bearer";

export const isMaskedSecret = (value: string | null | undefined) =>
  value === WEBHOOK_SECRET_MASK;

export const pickWebhookAuthValues = (webhook: any): WebhookAuthValues => ({
  auth_type: webhook?.auth_type ?? null,
  auth_endpoint: webhook?.auth_endpoint ?? null,
  auth_body: webhook?.auth_body ?? null,
  auth_token_path: webhook?.auth_token_path ?? null,
});

/** Blank form values map to null; auth_body "" would fail the server's JSON parse. */
const toWireValue = (field: WebhookAuthField, value: string | null) => {
  if (value === null || value === undefined) return null;
  if (field === "auth_type") return normalizeAuthType(value) || null;
  if (field === "auth_endpoint") return value.trim() || null;
  return value.trim() === "" ? null : value;
};

/** Client-side JSON check for auth_body; null when fine or not checkable. */
export const authBodyJsonError = (value: string | null | undefined) => {
  if (!value || !value.trim() || isMaskedSecret(value)) return null;
  try {
    JSON.parse(value);
    return null;
  } catch {
    return "Auth Body must be valid JSON.";
  }
};

/**
 * Apply one edited auth field to a PATCH object.
 *
 * A value equal to what the server returned is dropped from the patch, so an
 * untouched (or restored) masked secret is never sent back.
 */
export const applyWebhookAuthFieldToPatch = <T extends Record<string, any>>(
  patch: T,
  field: WebhookAuthField,
  value: string | null,
  initialValue: string | null
): T => {
  const next: Record<string, any> = { ...patch };
  const wireValue = toWireValue(field, value);
  if (wireValue === toWireValue(field, initialValue) || isMaskedSecret(value)) {
    delete next[field];
  } else {
    next[field] = wireValue;
  }
  return next as T;
};

/**
 * Auth fields for a create (POST). Only the fields the chosen auth type uses
 * are sent; endpoint and token path are Bearer-only.
 */
export const buildWebhookAuthCreatePayload = (
  values: WebhookAuthValues
): Partial<WebhookAuthValues> => {
  const authType = normalizeAuthType(values.auth_type);
  if (!authType) return { auth_type: null };
  const payload: Partial<WebhookAuthValues> = {
    auth_type: authType,
    auth_body: toWireValue("auth_body", values.auth_body),
  };
  if (authType === "bearer") {
    payload.auth_endpoint = toWireValue("auth_endpoint", values.auth_endpoint);
    payload.auth_token_path = toWireValue(
      "auth_token_path",
      values.auth_token_path
    );
  }
  return payload;
};

/**
 * DRF 400 body -> { field: first message }. Returns {} for anything that is
 * not a field-keyed 400 (the caller then falls back to its generic handling).
 */
export const extractWebhookFieldErrors = (
  reason: AxiosError | any
): WebhookFieldErrors => {
  if (reason?.response?.status !== 400) return {};
  const data = reason.response.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  const errors: WebhookFieldErrors = {};
  Object.entries(data).forEach(([field, messages]) => {
    if (Array.isArray(messages) && messages.length > 0) {
      errors[field] = String(messages[0]);
    } else if (typeof messages === "string" && messages) {
      errors[field] = messages;
    }
  });
  return errors;
};
